import type { TocMenuItem } from "../types/menu";
import { getIconElement } from "../config/iconRegistry";

/**
 * Menu item structure for sidebar rendering
 */
export interface SidebarMenuItem {
  key: string;
  label: string;
  icon?: React.ReactElement;
  route?: string;
  children?: SidebarMenuItem[];
  /** ItemType name for navigation (e.g., "Lead", "Opportunity") */
  itemTypeName?: string;
  /** Action type: ItemType, Dashboard, Form, Category */
  action?: string;
  /** Sort order */
  sortOrder: number;
}

/**
 * Get localized label based on current language
 */
export function getLocalizedLabel(item: TocMenuItem, language: string): string {
  if (language === "vi" && item.labelVi) {
    return item.labelVi;
  }
  if (language === "en" && item.labelEn) {
    return item.labelEn;
  }
  return item.label || item.name;
}

/**
 * Map icon name to icon registry key
 */
const ICON_MAPPINGS: Record<string, string> = {
  // CRM entities
  lead: "team",
  leads: "team",
  sep_lead: "team",
  opportunity: "rocket",
  opportunities: "rocket",
  sep_opportunity: "rocket",
  sale: "rocket",
  sales: "rocket",
  customer: "user",
  customers: "user",
  sep_customer: "user",
  organization: "bank",
  organizations: "bank",
  sep_organization: "bank",
  company: "bank",
  companies: "bank",
  contact: "contacts",
  contacts: "contacts",
  person: "contacts",
  persons: "contacts",
  // Design/PLM
  product: "shop",
  products: "shop",
  part: "container",
  parts: "container",
  document: "file-text",
  documents: "file-text",
  // My Innovator
  inbasket: "inbox",
  "my inbasket": "inbox",
  myinbasket: "inbox",
  "in basket": "inbox",
  assignment: "solution",
  assignments: "solution",
  "activity assignment": "solution",
  activity: "play-circle",
  activities: "play-circle",
  "my activities": "play-circle",
  // General
  folder: "folder",
  defaultitemtype: "appstore", // Generic ItemType icon
  itemtype: "appstore",
  dashboard: "dashboard",
  report: "fund-projection-screen",
  reports: "fund-projection-screen",
  workflow: "apartment",
  task: "check-square",
  tasks: "check-square",
  calendar: "calendar",
  schedule: "schedule",
  setting: "setting",
  settings: "setting",
  admin: "setting",
  administration: "setting",
  crm: "solution",
};

/**
 * Map Aras icon path to icon registry key
 * Aras icons are typically SVG paths like "../images/Lead.svg"
 * We extract the icon name and try to map it
 *
 * @param icon - Icon path from Aras (e.g., "../images/Lead.svg")
 * @param itemTypeName - Optional ItemType name for fallback mapping
 */
function mapArasIconToKey(
  icon: string | undefined,
  itemTypeName?: string,
): string {
  // Extract icon name from path if provided
  let iconName = "";
  if (icon) {
    // e.g., "../images/Lead.svg" -> "lead"
    // e.g., "Lead" -> "lead"
    iconName = icon
      .replace(/^.*[\\/]/, "") // Remove path
      .replace(/\.(svg|png|ico)$/i, "") // Remove extension
      .toLowerCase();
  }

  // Check if icon maps to something specific
  const mappedIcon = ICON_MAPPINGS[iconName];
  if (mappedIcon && mappedIcon !== "appstore") {
    return mappedIcon;
  }

  // If icon is generic (DefaultItemType.svg) or not found,
  // try to use itemTypeName for better icon
  if (itemTypeName) {
    const itemTypeKey = itemTypeName.toLowerCase().replace(/[^a-z0-9]/g, "");
    const itemTypeIcon = ICON_MAPPINGS[itemTypeKey];
    if (itemTypeIcon) {
      return itemTypeIcon;
    }
  }

  // Return mapped icon if we have one (including generic appstore)
  if (mappedIcon) {
    return mappedIcon;
  }

  // Final fallback
  return "folder";
}

/**
 * Extract ItemType name from TOC item name field as a fallback.
 * Pattern: "com.aras.innovator.cui_default.toc_{ItemTypeName}_{GUID32}"
 * Example: "...toc_Lead_4924FE241DF745F4943F80D9C13CC60E" → "Lead"
 * Example: "...toc_PE_Deviation_23E67357769FF83BDCCE6E59844685A7" → "PE_Deviation"
 */
function extractItemTypeNameFromTocName(name: string): string | undefined {
  const match = name.match(/\.toc_(.+)_[A-Fa-f0-9]{32}$/);
  return match?.[1];
}

/**
 * Get route for menu item.
 * Falls back to extracting ItemType name from the name field when
 * itemTypeName is not populated by the backend (e.g., user lacks
 * permission to read ItemType definitions from Aras).
 */
function getRoute(item: TocMenuItem): string | undefined {
  if (item.action === "ItemType") {
    const typeName =
      item.itemTypeName || extractItemTypeNameFromTocName(item.name);
    if (typeName) {
      return `/${typeName}`;
    }
  }
  if (item.action === "Dashboard" && item.url) {
    return item.url;
  }
  // Category items don't have routes
  return undefined;
}

/**
 * Transform TocMenuItem to SidebarMenuItem
 */
export function transformMenuItem(
  item: TocMenuItem,
  language: string,
): SidebarMenuItem {
  const iconKey = mapArasIconToKey(item.icon, item.itemTypeName);

  const menuItem: SidebarMenuItem = {
    key: item.id,
    label: getLocalizedLabel(item, language),
    icon: getIconElement(iconKey),
    route: getRoute(item),
    itemTypeName: item.itemTypeName,
    action: item.action,
    sortOrder: item.sortOrder,
  };

  // Transform children recursively
  if (item.children && item.children.length > 0) {
    menuItem.children = item.children.map((child) =>
      transformMenuItem(child, language),
    );
  }

  return menuItem;
}

/**
 * Transform TOC menu items to sidebar menu structure
 */
export function transformMenuToSidebar(
  items: TocMenuItem[],
  language: string = "vi",
): SidebarMenuItem[] {
  return items.map((item) => transformMenuItem(item, language));
}

/**
 * Flatten hierarchical menu items (for searching/filtering)
 */
export function flattenMenuItems(items: SidebarMenuItem[]): SidebarMenuItem[] {
  const result: SidebarMenuItem[] = [];

  function flatten(menuItems: SidebarMenuItem[]) {
    for (const item of menuItems) {
      result.push(item);
      if (item.children) {
        flatten(item.children);
      }
    }
  }

  flatten(items);
  return result;
}

/**
 * Find menu item by route
 */
export function findMenuItemByRoute(
  items: SidebarMenuItem[],
  route: string,
): SidebarMenuItem | undefined {
  const flattened = flattenMenuItems(items);
  return flattened.find((item) => item.route === route);
}

/**
 * Find menu item by ItemType name
 */
export function findMenuItemByItemType(
  items: SidebarMenuItem[],
  itemTypeName: string,
): SidebarMenuItem | undefined {
  const flattened = flattenMenuItems(items);
  return flattened.find((item) => item.itemTypeName === itemTypeName);
}
